import React, {useRef, useEffect, useState, useCallback} from 'react'
import PropTypes from 'prop-types'

import loadable from '@loadable/component'
const LiteYouTubeEmbed = loadable(() => import('react-lite-youtube-embed'))

import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {PlayIcon} from '../icons'
import {remCalc} from '../../theme/_utils'
import LoadingSpinner from '../loading-spinner'
import {BRAND_KEY} from '../../brand'
import {useIntl} from 'react-intl'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {video_gtm_event} from '../../custom-api/layers/default'

/**
 * Extracts the YouTube video ID from a given URL.
 *
 * @param {string} url - The URL of the YouTube video.
 * @returns {string|null} The YouTube video ID if found, otherwise null.
 */
const getYoutubeVideoId = (url) => {
    if (typeof url === 'string' && url.length === 11) {
        return url
    }
    var regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\\&v=)([^#\\&\\?]*).*/
    var match = url?.match(regExp)
    if (match && match[2].length == 11) {
        return match[2]
    } else {
        return null
    }
}

/**
 * A React component that renders a YouTube iframe using react-lite-youtube-embed.
 * Improves performance and Core Web Vitals by deferring iframe load until user interaction.
 * When used inside a Swiper slider, the video is only rendered when the slide is visible.
 *
 * @param {Object} props - The component props.
 * @param {string} props.title - The title of the video.
 * @param {string} props.videoSrc - The source URL of the YouTube video.
 * @param {string} [props.thumbnail] - Optional thumbnail image URL.
 * @param {Function} [props.onPause] - Callback fired when the video is paused.
 * @param {Function} [props.onPlay] - Callback fired when the video starts playing.
 * @param {boolean} [props.isVariantStyle] - Whether the variant style is applied.
 * @param {boolean} [props.enableInteractionHandlers] - Whether to enable play/pause interaction handlers.
 * @param {string} [props.contentType] - The content type, used for GTM tracking.
 * @returns {JSX.Element} The rendered YouTube iframe component.
 */
const YoutubeIframe = ({
    title,
    videoSrc,
    thumbnail = null,
    onPause,
    onPlay,
    isVariantStyle,
    enableInteractionHandlers = false,
    contentType = '',
}) => {
    const YoutubeVideoId = getYoutubeVideoId(videoSrc)
    const ref = useRef()
    const playerRef = useRef(null)
    const [dimensions, setDimensions] = useState({loaded: false})
    const [showVideo, setShowVideo] = useState(thumbnail ? false : true)
    const [isVisible, setIsVisible] = useState(false)
    const {formatMessage} = useIntl()
    const isKenwood = BRAND_KEY === 'kenwood'
    const {addLayer} = useGoogleTagManager()

    /**
     * Uses IntersectionObserver to detect when the component enters the viewport.
     * The video is only rendered once the slide becomes visible,
     * avoiding unnecessary loading of off-screen slides.
     */
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true)
                    // Once visible, no need to keep observing
                    observer.disconnect()
                }
            },
            {threshold: 0.5}, // Trigger when at least 50% of the slide is visible
        )

        if (ref.current) {
            observer.observe(ref.current)
        }

        return () => observer.disconnect()
    }, [])

    /**
     * Without a custom thumbnail (e.g. the PDP gallery) the YouTube iframe is loaded as soon as
     * the slide is visible, so the user starts playback with YouTube's own play button.
     * Creating the iframe on tap and relying on autoplay does not work on iOS Safari,
     * which does not pass the tap on to a freshly created cross-origin iframe.
     */
    const alwaysLoadIframe = !thumbnail

    // Keeps the latest state handler for the YT.Player, which is created only once
    const handleStateChangeRef = useRef(null)

    /**
     * Loads the YouTube IFrame API and attaches the player to the iframe
     * once LiteYouTubeEmbed has added it to the DOM.
     */
    const attachPlayer = useCallback(() => {
        const initPlayer = () => {
            const iframe = ref.current?.querySelector('iframe')
            if (!iframe || playerRef.current) return

            // Attach the YT.Player instance to the existing iframe
            playerRef.current = new window.YT.Player(iframe, {
                events: {
                    onStateChange: (event) => handleStateChangeRef.current?.(event),
                },
            })
        }

        // If the API is already loaded, initialize the player directly
        if (window.YT && window.YT.Player) {
            initPlayer()
            return
        }

        // Inject the YouTube IFrame API script into the page
        const tag = document.createElement('script')
        tag.src = 'https://www.youtube.com/iframe_api'
        const firstScriptTag = document.getElementsByTagName('script')[0]
        if (firstScriptTag?.parentNode) {
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
        } else {
            document.head.appendChild(tag)
        }

        // Initialize the player once the API is ready
        window.onYouTubeIframeAPIReady = () => {
            initPlayer()
        }
    }, [])

    /**
     * Maps YouTube player state codes to GTM event action strings
     * and pushes the corresponding event to the data layer.
     *
     * @param {Object} event - The YouTube player state change event.
     */
    const addGTMVideoEvent = (event) => {
        var videoStateToGTM = null
        switch (event.data) {
            case window.YT?.PlayerState?.PLAYING:
                videoStateToGTM = 'video-play'
                break
            case window.YT?.PlayerState?.PAUSED:
                videoStateToGTM = 'video-pause'
                break
            case window.YT?.PlayerState?.ENDED:
                videoStateToGTM = 'video-complete'
                break
            default:
                break
        }

        if (videoStateToGTM) {
            addLayer(
                video_gtm_event.bind(this, {
                    event: 'video',
                    eventCategory: 'engagement',
                    eventAction: videoStateToGTM,
                    videoTitle: title || '',
                    videoId: YoutubeVideoId || '',
                    videoUrl: videoSrc || '',
                    howTo: contentType === 'howto' ? 'true' : '',
                }),
            )
        }
    }

    /**
     * Handles YouTube player state changes.
     * Triggers onPlay/onPause callbacks when interaction handlers are enabled,
     * and sends GTM tracking events for all state changes.
     *
     * @param {Object} event - The YouTube player state change event.
     */
    const handleStateChange = (event) => {
        if (
            enableInteractionHandlers &&
            event.data === window.YT?.PlayerState?.PLAYING &&
            onPlay &&
            isVariantStyle
        ) {
            onPlay()
        }
        if (enableInteractionHandlers && event.data === window.YT?.PlayerState?.PAUSED && onPause) {
            onPause()
        }

        addGTMVideoEvent(event)
    }
    handleStateChangeRef.current = handleStateChange

    /**
     * Uses ResizeObserver to track the container dimensions.
     * More precise than window resize listener as it observes
     * the element directly instead of the entire window.
     */
    useEffect(() => {
        if (!ref.current) return

        const observer = new ResizeObserver(([entry]) => {
            const {height} = entry.contentRect
            setDimensions({height, loaded: true})
        })

        observer.observe(ref.current)

        return () => observer.disconnect()
    }, [])

    // Destroy the YT.Player instance when the component unmounts
    useEffect(() => {
        return () => {
            if (playerRef.current) {
                playerRef.current.destroy?.()
            }
        }
    }, [])

    return (
        <Box
            ref={ref}
            w="100%"
            h={isVariantStyle ? {base: '100vw', md: '100%'} : '100%'}
            data-testid="video-container"
        >
            {/* Custom thumbnail button shown before the user triggers video playback */}
            {!showVideo && thumbnail && (
                <Box
                    as="button"
                    onClick={() => setShowVideo(true)}
                    aria-label={formatMessage({
                        id: 'youtube_video.play_button.aria_label',
                        defaultMessage: 'Play video',
                    })}
                    style={{
                        backgroundImage: `url(${thumbnail})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        cursor: 'pointer',
                    }}
                >
                    <PlayIcon
                        height={remCalc(40)}
                        width={remCalc(40)}
                        fill="background.default"
                        color={isKenwood ? 'background.alternative' : 'currentColor'}
                    />
                </Box>
            )}

            {/* Show a loading spinner while dimensions are being calculated */}
            {!dimensions.loaded && !showVideo && (
                <LoadingSpinner data-testid="loading-spinner-video" />
            )}

            {/* Render LiteYouTubeEmbed only when dimensions are ready,
                the video should be shown and the slide is visible in the viewport */}
            {dimensions.loaded && showVideo && YoutubeVideoId && isVisible && (
                <Box
                    position="relative"
                    w="100%"
                    h="100%"
                    sx={{
                        // Hide the default play button since we use a custom thumbnail
                        //'.lty-playbtn': {display: 'none'},
                        // Override default aspect-ratio behavior to fill the parent container
                        '.yt-lite': {
                            width: '100%',
                            height: '100%',
                            maxWidth: '100%',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            paddingBottom: '0 !important',
                        },
                        '.yt-lite iframe': {
                            width: '100%',
                            height: '100%',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                        },
                    }}
                >
                    <LiteYouTubeEmbed
                        id={YoutubeVideoId}
                        title={title || 'YouTube video'}
                        poster="maxresdefault"
                        thumbnail={thumbnail || undefined}
                        alwaysLoadIframe={alwaysLoadIframe}
                        // adds enablejsapi=1 and the page origin, required to attach the YT.Player API
                        enableJsApi
                        // autoplay=0 ensures the video does not start automatically
                        params={alwaysLoadIframe ? undefined : 'autoplay=0'}
                        onIframeAdded={attachPlayer}
                        iframeClass="youtube-iframe"
                        wrapperClass="yt-lite"
                        data-testid="youtube-video"
                    />
                </Box>
            )}
        </Box>
    )
}

YoutubeIframe.propTypes = {
    title: PropTypes.string,
    videoSrc: PropTypes.string,
    thumbnail: PropTypes.string,
    onBlur: PropTypes.func,
    onPause: PropTypes.func,
    onPlay: PropTypes.func,
    enableInteractionHandlers: PropTypes.bool,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isVariantStyle: PropTypes.bool,
    contentType: PropTypes.string,
}

export default YoutubeIframe
